if (!customElements.get('klaviyo-subscribe-form')) {
  class KlaviyoSubscribeForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.submitBtn = this.querySelector('[data-submit]');
      this.errorEl = this.querySelector('[data-error]');
      this.container = this.closest('[data-lead-magnet]');
      this.companyId = this.dataset.companyId;
      this.listId = this.dataset.listId;
      this.source = this.dataset.source || 'Lead Magnet';
      // Phone routing: 'klaviyo' (default — unchanged behaviour for the
      // gold/silver/youtube forms) or 'attentive' (giveaway form).
      this.phoneTarget = (this.dataset.phoneTarget || 'klaviyo').toLowerCase();
      this.attentiveEndpoint = this.dataset.attentiveEndpoint || '';
      this.attentiveSource = this.dataset.attentiveSource || this.source;
    }

    connectedCallback() {
      if (!this.form) return;
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }

    async handleSubmit(event) {
      event.preventDefault();

      if (!this.companyId || !this.listId) {
        this.showError('Form is not configured yet. Please contact support.');
        return;
      }

      const formData = new FormData(this.form);
      const email = (formData.get('email') || '').toString().trim();
      const phone = (formData.get('phone') || '').toString().trim();
      const firstName = (formData.get('first_name') || '').toString().trim();
      const lastName = (formData.get('last_name') || '').toString().trim();

      if (!this.isValidEmail(email)) {
        this.showError('Please enter a valid email address.');
        return;
      }

      this.hideError();
      this.setLoading(true);

      const profileAttributes = { email };
      if (firstName) profileAttributes.first_name = firstName;
      if (lastName) profileAttributes.last_name = lastName;

      // Normalize phone once, then route per data-phone-target.
      // Default ('klaviyo') keeps the original behaviour; 'attentive' sends the
      // phone to Attentive instead and omits it from the Klaviyo profile.
      const normalizedPhone = phone ? this.normalizePhone(phone) : '';
      if (normalizedPhone && this.phoneTarget !== 'attentive') {
        profileAttributes.phone_number = normalizedPhone;
      }

      // Quiz answers -> Klaviyo custom properties + lead score (no-op on forms
      // without scored radios, so other landing pages are unaffected).
      const quizProps = this.collectQuiz();
      if (Object.keys(quizProps).length) {
        profileAttributes.properties = quizProps;
      }

      // Klaviyo's Subscribe API rejects the `subscriptions` field both inside
      // the profile resource and at the subscription resource level for this
      // account's API revision. POSTing to /client/subscriptions/ with a valid
      // profile + list relationship implies the subscription action; the list's
      // configured opt-in settings in Klaviyo determine the consent state.
      const body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: this.source,
            profile: {
              data: {
                type: 'profile',
                attributes: profileAttributes,
              },
            },
          },
          relationships: {
            list: { data: { type: 'list', id: this.listId } },
          },
        },
      };

      try {
        const res = await fetch(
          `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.companyId)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              revision: '2024-10-15',
            },
            body: JSON.stringify(body),
          }
        );

        if (res.status === 202 || res.ok) {
          this.showSuccess();
          this.maybeSendToAttentive({ phone: normalizedPhone, email, firstName, lastName });
          this.dispatchEvent(
            new CustomEvent('klaviyo:subscribed', {
              bubbles: true,
              detail: { email, listId: this.listId },
            })
          );
        } else {
          const payload = await res.json().catch(() => null);
          const detail =
            payload && payload.errors && payload.errors[0] && payload.errors[0].detail;
          this.showError(detail || 'Something went wrong. Please try again.');
        }
      } catch (err) {
        this.showError('Network error. Please try again.');
      } finally {
        this.setLoading(false);
      }
    }

    // Reads scored quiz fields in the form — dropdowns (select[data-quiz-field])
    // and/or radio groups (input[type=radio][data-points]). Each answer becomes
    // a Klaviyo profile property keyed by the field's `name` (e.g. buyer_status),
    // and the points sum into lead_score. No-op on forms without these fields.
    collectQuiz() {
      const props = {};
      let score = 0;
      let answered = false;

      this.form.querySelectorAll('select[data-quiz-field]').forEach((sel) => {
        if (!sel.value) return;
        const opt = sel.options[sel.selectedIndex];
        answered = true;
        if (sel.name) props[sel.name] = sel.value;
        score += parseInt(opt && opt.dataset.points, 10) || 0;
      });

      this.form.querySelectorAll('input[type="radio"][data-points]').forEach((r) => {
        if (!r.checked) return;
        answered = true;
        if (r.name) props[r.name] = r.value;
        score += parseInt(r.dataset.points, 10) || 0;
      });

      if (answered) {
        props.lead_score = score; // number, so Klaviyo range segments work
        props.lead_quality = score >= 8 ? 'High' : score >= 5 ? 'Medium' : 'Low';
      }
      return props;
    }

    // Hands the phone (+ contact details) to the Attentive module, if this form
    // is configured to route phone there. Non-blocking; never affects the
    // already-shown success state.
    maybeSendToAttentive(data) {
      if (this.phoneTarget !== 'attentive' || !data.phone) return;
      if (window.SummitAttentive && typeof window.SummitAttentive.subscribe === 'function') {
        window.SummitAttentive.subscribe({
          endpoint: this.attentiveEndpoint,
          source: this.attentiveSource,
          phone: data.phone,
          email: data.email,
          first_name: data.firstName,
          last_name: data.lastName,
        });
      } else if (this.attentiveEndpoint) {
        console.warn('[klaviyo-subscribe] Attentive module not loaded; phone not sent to SMS.');
      }
    }

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    normalizePhone(value) {
      const trimmed = value.replace(/[^\d+]/g, '');
      if (!trimmed) return '';
      if (trimmed.startsWith('+')) return trimmed;
      if (trimmed.length === 11 && trimmed.startsWith('1')) return `+${trimmed}`;
      if (trimmed.length === 10) return `+1${trimmed}`;
      return `+${trimmed}`;
    }

    setLoading(loading) {
      if (!this.submitBtn) return;
      this.submitBtn.disabled = loading;
      this.submitBtn.setAttribute('aria-busy', loading ? 'true' : 'false');
    }

    showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.setAttribute('aria-hidden', 'false');
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.setAttribute('aria-hidden', 'true');
    }

    showSuccess() {
      if (this.container) this.container.setAttribute('data-state', 'success');
    }
  }

  customElements.define('klaviyo-subscribe-form', KlaviyoSubscribeForm);
}
